import { useState, useEffect, useCallback, useRef } from 'react';
import './DashboardPage.css';

function DocumentsPage() {
    const fileInputRef = useRef(null);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [uploadingType, setUploadingType] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isUploading, setIsUploading] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [session, setSession] = useState(() => {
        const stored = localStorage.getItem('userSession');
        return stored ? JSON.parse(stored) : null;
    });

    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchDocs = useCallback(() => {
        const storedSession = JSON.parse(localStorage.getItem('userSession'));
        if (storedSession && storedSession.userId) {
            setIsLoading(true);
            // Include requesting user info for authorization
            const url = `${API_URL}/api/documents/${storedSession.userId}?requestingUserId=${storedSession.userId}&requestingUserRole=${storedSession.role || 'user'}`;
            console.log('📄 [FRONTEND] Fetching documents:', url);

            fetch(url)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        const mappedDocs = data.documents.map(doc => ({
                            id: doc.id,
                            name: doc.file_name || doc.document_type,
                            type: doc.document_type,
                            uploadDate: new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                            status: doc.verification_status,
                            size: doc.file_size ? (doc.file_size / 1024 / 1024).toFixed(2) + ' MB' : '0 MB',
                            filePath: doc.file_path,
                            signedUrl: doc.signed_url
                        }));
                        console.log('✅ [FRONTEND] Documents mapped with URLs:', mappedDocs.map(d => ({ id: d.id, hasUrl: !!d.signedUrl })));
                        setDocuments(mappedDocs);
                    } else if (data.message) {
                        setMessage({ type: 'error', text: data.message });
                    }
                })
                .catch(err => {
                    console.error('Error fetching documents:', err);
                    setMessage({ type: 'error', text: 'Failed to load documents' });
                })
                .finally(() => setIsLoading(false));
        }
    }, [API_URL]);

    useEffect(() => {
        const stored = localStorage.getItem('userSession');
        if (stored) setSession(JSON.parse(stored));
        fetchDocs();
    }, [fetchDocs]);

    const handleFileUploadClick = (type) => {
        console.log('📤 [UPLOAD-CLICK] Document type:', type);
        const currentSession = session || JSON.parse(localStorage.getItem('userSession'));
        console.log('👤 [UPLOAD-CLICK] Current session:', currentSession);
        const role = (currentSession?.role || '').toLowerCase();

        if (type === 'Signed Agreement' && role !== 'admin' && role !== 'super_admin') {
            setMessage({ type: 'error', text: 'Only an admin or super admin can upload the Signed Agreement.' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Check if this document type has already been uploaded 2 times
        const uploadCount = documents.filter(d => d.type === type).length;
        console.log(`📊 [UPLOAD-CLICK] Upload count for "${type}":`, uploadCount, 'out of', documents.length, 'total documents');

        const limit = type === 'Extra Documents' ? 10 : 2;

        if (uploadCount >= limit) {
            console.warn('🚫 [UPLOAD-CLICK] Upload limit reached for', type);
            setMessage({
                type: 'error',
                text: `⚠️ Upload Limit Reached: You can only upload "${type}" ${limit} times. Please ensure you upload the correct document. If you need to replace it, please contact support.`
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        console.log('✅ [UPLOAD-CLICK] Opening file picker for', type);
        setUploadingType(type);
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        console.log('📎 [FILE-CHANGE] File input changed');
        const file = e.target.files[0];
        console.log('📎 [FILE-CHANGE] Selected file:', file?.name, 'Type:', file?.type, 'Size:', file?.size);
        console.log('📎 [FILE-CHANGE] Uploading type:', uploadingType);

        if (!file || !uploadingType) {
            console.warn('⚠️ [FILE-CHANGE] No file or uploadingType missing');
            return;
        }

        const currentSession = session || JSON.parse(localStorage.getItem('userSession'));
        console.log('👤 [FILE-CHANGE] Current session:', currentSession);

        if (!currentSession) {
            setMessage({ type: 'error', text: 'Please log in again to upload documents.' });
            return;
        }

        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'video/mp4', 'video/quicktime', 'video/x-msvideo'];
        if (!allowedTypes.includes(file.type)) {
            console.error('❌ [FILE-CHANGE] Invalid file type:', file.type);
            setMessage({ type: 'error', text: 'Only PDF, JPEG, JPG, PNG, MP4, MOV, and AVI files are allowed!' });
            e.target.value = '';
            return;
        }

        // Validate file size (5MB for docs/images, 50MB for videos)
        const isVideo = file.type.startsWith('video/');
        const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
        const sizeLabel = isVideo ? '50MB' : '5MB';

        if (file.size > maxSize) {
            console.error('❌ [FILE-CHANGE] File too large:', file.size, 'Max:', maxSize);
            setMessage({ type: 'error', text: `File size must be less than ${sizeLabel}!` });
            e.target.value = '';
            return;
        }

        try {
            setIsUploading(true);
            setMessage({ type: 'info', text: `Uploading ${uploadingType}...` });
            console.log('🚀 [FILE-CHANGE] Starting upload...');

            const formData = new FormData();
            formData.append('document', file);
            formData.append('userId', currentSession.userId);
            formData.append('documentType', uploadingType);
            formData.append('requestingUserId', currentSession.userId);
            formData.append('requestingUserRole', currentSession.role || 'user');

            console.log('📤 [FILE-CHANGE] Uploading to:', `${API_URL}/api/documents/upload`);
            console.log('📤 [FILE-CHANGE] FormData:', {
                userId: currentSession.userId,
                documentType: uploadingType,
                fileName: file.name
            });

            const response = await fetch(`${API_URL}/api/documents/upload`, {
                method: 'POST',
                body: formData
            });

            console.log('📥 [FILE-CHANGE] Response status:', response.status);
            const data = await response.json();
            console.log('📥 [FILE-CHANGE] Response data:', data);

            if (data.success) {
                console.log('✅ [FILE-CHANGE] Upload successful!');
                setMessage({ type: 'success', text: `${uploadingType} uploaded successfully!` });
                fetchDocs();
                // Auto-dismiss success message after 3 seconds
                setTimeout(() => {
                    setMessage({ type: '', text: '' });
                }, 3000);
            } else {
                console.error('❌ [FILE-CHANGE] Upload failed:', data.message);
                setMessage({ type: 'error', text: data.message || 'Upload failed.' });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            console.error('💥 [FILE-CHANGE] Upload error:', err);
            setMessage({ type: 'error', text: 'Upload error. Connection failed.' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsUploading(false);
            setUploadingType(null);
            e.target.value = '';
            console.log('🏁 [FILE-CHANGE] Upload process completed');
        }
    };

    const handlePreview = (doc) => {
        console.log('👁️ [PREVIEW] Previewing document:', { id: doc.id, name: doc.name, hasUrl: !!doc.signedUrl, url: doc.signedUrl ? 'Present' : 'Missing' });
        setPreviewDocument(doc);
    };

    const closePreview = () => {
        setPreviewDocument(null);
    };

    const getDocStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'verified': return '#16a34a';
            case 'pending': return '#f59e0b';
            case 'rejected': return '#ef4444';
            default: return '#64748b';
        }
    };

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (ext === 'pdf') return '📄';
        if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️';
        if (['mp4', 'mov', 'avi'].includes(ext)) return '🎥';
        return '📎';
    };

    return (
        <div className="dashboard-page-new">
            <div className="welcome-section">
                <div>
                    <h1>Document Vault</h1>
                    <p className="welcome-subtitle">Securely store and manage your financial documents</p>
                </div>
                <button className="btn-primary" onClick={() => handleFileUploadClick('General Document')} disabled={isUploading}>
                    {isUploading ? '⏳ Uploading...' : '📤 Upload New'}
                </button>
            </div>

            {message.text && (
                <div style={{
                    padding: '1.25rem',
                    borderRadius: '12px',
                    backgroundColor: message.type === 'success' ? '#dcfce7' : message.type === 'error' ? '#fee2e2' : '#fffbea',
                    color: message.type === 'success' ? '#16a34a' : message.type === 'error' ? '#ef4444' : '#f59e0b',
                    border: message.type === 'error' ? '2px solid #ef4444' : 'none',
                    marginBottom: '1.5rem',
                    fontWeight: '600',
                    fontSize: '1rem',
                    textAlign: 'center',
                    position: 'relative',
                    boxShadow: message.type === 'error' ? '0 4px 6px rgba(239, 68, 68, 0.2)' : 'none'
                }}>
                    {message.text}
                    <button
                        onClick={() => setMessage({ type: '', text: '' })}
                        style={{
                            position: 'absolute',
                            right: '1rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: 'inherit',
                            opacity: 0.7,
                            lineHeight: 1
                        }}
                        title="Dismiss"
                    >
                        ×
                    </button>
                </div>
            )}

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.avi"
            />

            {/* Document Checklist */}
            <div className="content-section">
                <div className="section-header-new">
                    <h2>KYC Checklist</h2>
                </div>
                <div className="quick-actions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                    {[
                        { name: 'Aadhar Card', icon: '🆔' },
                        { name: 'PAN Card', icon: '💳' },
                        { name: 'Photo', icon: '📸' },
                        { name: 'Residence Proof', icon: '🏠' },
                        { name: 'Credit Score', icon: '📊' },
                        { name: 'Bank Statement', icon: '🏦' },
                        { name: 'Salary Slip', icon: '💼' },
                        { name: 'Signature', icon: '📝' },
                        { name: 'Extra Documents', icon: '📋' },
                        { name: 'Signed Agreement', icon: '📑', adminOnly: true }
                    ].map(doc => {
                        const uploadCount = documents.filter(d => d.type === doc.name).length;
                        const limit = doc.name === 'Extra Documents' ? 10 : 2;
                        const isLimitReached = uploadCount >= limit;
                        const isAdmin = (session?.role || '').toLowerCase() === 'admin' || (session?.role || '').toLowerCase() === 'super_admin';
                        const isLocked = doc.adminOnly && !isAdmin;
                        const isDisabled = isUploading || isLocked || isLimitReached;

                        return (
                            <div
                                key={doc.name}
                                className="action-card"
                                onClick={() => !isDisabled && handleFileUploadClick(doc.name)}
                                style={{
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.6 : 1,
                                    border: isLimitReached ? '2px solid #ef4444' : undefined
                                }}
                                title={isLocked ? 'Only admin/super admin can upload this' : isLimitReached ? `Upload limit reached (${limit}/${limit})` : undefined}
                            >
                                <div className="action-icon">{doc.icon}</div>
                                <span>{doc.name}</span>
                                <div style={{
                                    fontSize: '0.75rem',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    background: isLimitReached ? '#fee2e2' : uploadCount > 0 ? '#dcfce7' : '#f1f5f9',
                                    color: isLimitReached ? '#ef4444' : uploadCount > 0 ? '#16a34a' : '#64748b',
                                    marginTop: '5px',
                                    fontWeight: '700'
                                }}>
                                    {isLimitReached ? `🚫 ${limit}/${limit} LIMIT` : uploadCount > 0 ? `✓ ${uploadCount}/${limit}` : (doc.adminOnly ? 'ADMIN ONLY' : 'REQUIRED')}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Guarantor Documents Section */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Guarantor Documents</h2>
                    <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '0.5rem 0 0 0' }}>Upload guarantor's identity and verification documents</p>
                </div>
                <div className="quick-actions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                    {[
                        { name: 'Guarantor Aadhar Card', icon: '🆔' },
                        { name: 'Guarantor PAN Card', icon: '💳' },
                        { name: 'Guarantor Photo', icon: '📸' },
                        { name: 'Guarantor Residence Proof', icon: '🏠' },
                        { name: 'Guarantor Bank Statement', icon: '🏦' },
                        { name: 'Guarantor Signature', icon: '📝' },
                        { name: 'Guarantor Video', icon: '🎥' }
                    ].map(doc => {
                        const uploadCount = documents.filter(d => d.type === doc.name).length;
                        const isLimitReached = uploadCount >= 2;
                        const isDisabled = isUploading || isLimitReached;

                        return (
                            <div
                                key={doc.name}
                                className="action-card"
                                onClick={() => !isDisabled && handleFileUploadClick(doc.name)}
                                style={{
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.6 : 1,
                                    border: isLimitReached ? '2px solid #ef4444' : undefined
                                }}
                                title={isLimitReached ? 'Upload limit reached (2/2)' : undefined}
                            >
                                <div className="action-icon">{doc.icon}</div>
                                <span style={{ fontSize: '0.85rem' }}>{doc.name}</span>
                                <div style={{
                                    fontSize: '0.75rem',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    background: isLimitReached ? '#fee2e2' : uploadCount > 0 ? '#dcfce7' : '#fef3c7',
                                    color: isLimitReached ? '#ef4444' : uploadCount > 0 ? '#16a34a' : '#d97706',
                                    marginTop: '5px',
                                    fontWeight: '700'
                                }}>
                                    {isLimitReached ? '🚫 2/2 LIMIT' : uploadCount > 0 ? `✓ ${uploadCount}/2` : 'OPTIONAL'}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Documents List */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Your Uploads</h2>
                    <span className="loan-count">{documents.length} documents</span>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <div className="loading-spinner" style={{ border: '4px solid #fffbea', borderTop: '4px solid #f7bd20', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                        <p style={{ color: '#64748b', marginTop: '1rem' }}>Fetching documents...</p>
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : documents.length > 0 ? (
                    <div className="disbursement-table">
                        <div className="table-header" style={{ gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr 1fr' }}>
                            <div>File Name</div>
                            <div>Category</div>
                            <div>Upload Date</div>
                            <div>Size</div>
                            <div>Status</div>
                            <div>Action</div>
                        </div>
                        {documents.map((doc) => (
                            <div key={doc.id} className="table-row" style={{ gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1fr 1fr' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '1.5rem' }}>{getFileIcon(doc.name)}</span>
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <div style={{ fontWeight: '600' }}>{doc.name}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{doc.size}</div>
                                    </div>
                                </div>
                                <div>{doc.type}</div>
                                <div>{doc.uploadDate}</div>
                                <div>{doc.size}</div>
                                <div>
                                    <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '8px',
                                        fontSize: '0.7rem',
                                        fontWeight: '700',
                                        background: `${getDocStatusColor(doc.status)}15`,
                                        color: getDocStatusColor(doc.status),
                                        textTransform: 'uppercase'
                                    }}>
                                        {doc.status}
                                    </span>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <button
                                        className="btn-secondary"
                                        style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                                        onClick={() => handlePreview(doc)}
                                    >
                                        👁️ View
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '4rem', background: '#f8fafc', borderRadius: '16px' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📂</div>
                        <h3 style={{ margin: '0 0 0.5rem' }}>No documents found</h3>
                        <p style={{ color: '#64748b', margin: 0 }}>Start by uploading your KYC documents above.</p>
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            {previewDocument && (
                <div className="modal-overlay" onClick={closePreview}>
                    <div className="loan-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
                        <button className="modal-close" onClick={closePreview}>×</button>
                        <h2>{previewDocument.name}</h2>
                        <p className="modal-subtitle">{previewDocument.type} • {previewDocument.uploadDate}</p>

                        <div style={{ marginTop: '1.5rem', textAlign: 'center', maxHeight: '70vh', overflow: 'auto' }}>
                            {previewDocument.name.toLowerCase().endsWith('.pdf') ? (
                                <iframe
                                    src={previewDocument.signedUrl || `${API_URL}${previewDocument.filePath}`}
                                    style={{ width: '100%', height: '600px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                    title="Document Preview"
                                />
                            ) : previewDocument.name.toLowerCase().match(/\.(mp4|mov|avi)$/) ? (
                                <video
                                    controls
                                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                >
                                    <source src={previewDocument.signedUrl || `${API_URL}${previewDocument.filePath}`} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <img
                                    src={previewDocument.signedUrl || `${API_URL}${previewDocument.filePath}`}
                                    alt={previewDocument.name}
                                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                />
                            )}
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                            <button className="btn-secondary" onClick={closePreview} style={{ flex: 1 }}>
                                Close
                            </button>
                            <a
                                href={previewDocument.signedUrl || `${API_URL}/${previewDocument.filePath}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary"
                                style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                🌐 Open in New Tab
                            </a>
                            <a
                                href={previewDocument.signedUrl || `${API_URL}/${previewDocument.filePath}`}
                                download={previewDocument.name}
                                className="btn-secondary"
                                style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                ⬇️ Download
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DocumentsPage;
